/**
 * serviceComponentHole 常量集中文件
 *
 * 设计原则：库内所有“默认值 / 颜色 / 阈值 / 资源路径”集中在此文件，types/orchestrator 等
 * 模块禁止散落写死。业务方可通过 `initMultiComponent(serviceConfig)` 覆盖可覆盖项。
 *
 * --- DEFAULT_CONFIG 字段说明 ---
 * maxCount（4 种 type）              : 默认见 DEFAULT_CONFIG.maxCount，均可被 serviceConfig.maxCount 覆盖
 * componentMinGapMm                  : 组件间最小间隙（mm），可被 serviceConfig.componentMinGapMm 覆盖；0 = 允许外切
 * imageEditOpacity                   : 选中编辑时底图透明度，固定 0.5（不可覆盖，spec §4.3）
 * selectionBoxColor                  : 选中蓝虚线颜色
 * conflictColor                      : 冲突红虚线 / 红实线颜色
 * selectionLineDash                  : 选中/冲突虚线 lineDash
 * baseHoleCornerRadiusPercent        : 底座孔圆角百分比，固定从 DEFAULT_CONFIG 读取
 * liPaiSlotRangePercent              : 插口可移动范围百分比，固定从 DEFAULT_CONFIG 读取
 * canvasWhiteSpaceSafeRatio          : canvas 留白安全系数，按极限外探距离额外预留空间
 * baseHoleRotateIcon                 : 底座孔旋转 icon { src, size, marginTop }；src 为 reset.png；marginTop 为局部 +y（px）
 * baseHoleInteriorEpsilonPx          : R2 点-in-孔判定向内缩边（px），避免贴边浮点误报
 * baseHoleRectInsideSamplesPerEdge   : R1 每条矩形边分段数（2 → 8 探测点）
 * iconTapMoveThresholdPx             : icon 上 touchstart→touchend 总位移 < 该值视为 tap，否则 drag（spec §4.9）
 * componentMinHitSizePx              : 编辑态组件最小命中热区尺寸（px），只影响命中，不影响绘制/导出/碰撞
 * componentLargeHitPaddingPx         : 大组件命中热区额外 padding（px）
 * componentHitTieDistancePx          : 多候选距离近似阈值（px），近似时按绘制层级兜底
 * enableMagnifierGlobalDefault       : 全局放大镜开关默认值，可被 serviceConfig.enableMagnifierGlobal 覆盖
 */

import { POSITION } from '@/utils/constant.js';

/** 模式枚举 */
export const modeEdit = 'edit';
export const modeDraw = 'draw';

/** 组件类型枚举（与 layerData / 业务对齐） */
export const DRAW_TYPE = {
  KEY_HOLE: 'keyHole',
  LI_PAI: 'liPai',
  PERFORATION: 'perforation',
  BASE_HOLE: 'baseHole'
};

/** 沿用 serviceHoleNew 配色，保证视觉一致 */
export const lineColor = '#E60012'; // 刀线 / 冲突 / 效果图孔位
export const roundColor = '#407BFF'; // 钥匙扣编辑态圆环 / 选中蓝
export const lipaiColor = '#407BFF'; // 立牌色

/** 集中配置（业务可通过 serviceConfig 覆盖可覆盖项，详见文件顶部注释） */
export const DEFAULT_CONFIG = {
  /** 各类型组件数量上限；插口(liPai)强制 1 个不可覆盖 */
  maxCount: {
    [DRAW_TYPE.KEY_HOLE]: 5,
    [DRAW_TYPE.LI_PAI]: 1,
    [DRAW_TYPE.PERFORATION]: 5,
    [DRAW_TYPE.BASE_HOLE]: 5
  },
  /** 组件间最小间隙（mm），0 = 允许外切 */
  componentMinGapMm: 1,
  /** 穿孔/底座孔与刀线的最小间距（mm），0 = 允许相切 */
  componentToOutlineMinGapMm: 2.5,
  /** 底座孔圆角百分比，按高度计算 */
  baseHoleCornerRadiusPercent: 50,
  /** 插口可移动范围百分比，按 outline 轮廓宽度计算 */
  liPaiSlotRangePercent: 60,
  /** canvas 留白安全系数，1.1 = 极限外探距离额外预留 10% 空间 */
  canvasWhiteSpaceSafeRatio: 1.1,
  /** 选中编辑时底图不透明度 */
  imageEditOpacity: 0.5,
  /** 选中蓝色虚线框颜色 */
  selectionBoxColor: roundColor,
  /** 冲突红虚线/红实线颜色 */
  conflictColor: lineColor,
  /** 选中/冲突虚线 lineDash 数组 */
  selectionLineDash: [4, 2],
  /** 底座孔旋转 icon：src/大小/距离底座的距离(px) */
  baseHoleRotateIcon: {
    src: require('@/assets/image/reset.png'),
    size: 28,
    marginTop: 6
  },
  /** R2 点-in-孔判定向内缩边（px），避免贴边浮点误报 */
  baseHoleInteriorEpsilonPx: 0.5,
  /** R1 每条矩形边分段数（2 → 8 探测点） */
  baseHoleRectInsideSamplesPerEdge: 2,
  /** 底座孔是否支持旋转，默认关闭；开启后绘制旋转 icon 并响应旋转手势 */
  baseHoleRotationEnabled: false,
  /** icon 上 touch 总位移 < 该值视为 tap，否则 drag（px） */
  iconTapMoveThresholdPx: 5,
  /** 编辑态组件最小命中热区尺寸（px），只影响命中，不影响绘制/导出/碰撞 */
  componentMinHitSizePx: 56,
  /** 大组件命中热区额外 padding（px） */
  componentLargeHitPaddingPx: 2,
  /** 多候选距离近似阈值（px），近似时按绘制层级兜底 */
  componentHitTieDistancePx: 2,
  /** 全局放大镜开关默认值 */
  enableMagnifierGlobalDefault: true,
  /** 穿孔↔底座孔交叉偏移量（px）：当一种类型存在时，首次添加另一种类型向下偏移该值 */
  crossTypeOffsetPx: 30,
  //
  dpr: Math.min(window.devicePixelRatio || 1, 3)
};

/** 透出 POSITION 给外部调用方使用，避免外部再去 import constant.js */
export { POSITION };
